import { useForm } from 'react-hook-form';
import { Checkbox, FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import { Button, Container, Grid, Paper, Stack, Typography, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { formatDate, getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';
import {  COLLECTION_SAMPLE } from '../../../../../constants';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';

const BILL_TABLE_HEADER = [
  {
    header: 'Bill Id',
    accessor: 'bill_no'
  },
  {
    header: 'Type',
    accessor: 'type'
  },
  {
    header: 'Date',
    accessor: (row) => `${formatDate(row?.date)} ${row.time}` || 'null'
  },
  {
    header: 'Uhid No.',
    accessor: (row)=> row?.uhid || row?.patient?.uhid
  },
  {
    header: 'Ipd No.',
    accessor: 'ipd_no'
  },
  {
    header: 'Patient Name',
    accessor: (row)=> row?.patient_name
  },
  {
    header: 'Mobile No',
    accessor: (row)=> row?.mobile 
  },
  {
    header: 'Lab Ascession No',
    accessor: 'barcode_no'
  },
  {
    header: 'Lab Bill No',
    accessor: 'bill_no'
  }
];

const TEST_TABLE_HEADER = [
  {
    header: 'Test Name',
    accessor: (row) => row?.test_name?.test_name || row?.service_name
  },
  {
    header: 'Quantity',
    accessor: 'quantity'
  },
  {
    header: 'Rate',
    accessor: 'rate'
  },
  {
    header: 'Amount',
    accessor: 'amount'
  },
  {
    header: 'Discount Amount',
    accessor: 'disc_amt'
  },
  {
    header: 'Net Amount',
    accessor: 'net_amt'
  }
];

export const SampleCollection = () => {
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [collectionSampleFilter, setCollectionSampleFilter] = useState('Pending');
  const [billingData, setBillingData] = useState({
    loading: false,
    investigationBookings: [],
    opdBills: [],
    ipdBills: [],
  });
  const user = JSON.parse(localStorage.getItem('user'));

  const [selectedRows, setSelectedRows] = useState([]);
  
  const isSelected = (row) => {
    return row !== undefined && selectedRows.some((selectedRow) => selectedRow._id === row._id);
  };

  const handleRowSelect = (row) => {
    if (row === undefined) {
      console.warn('Row data is undefined. Check data structure.');
      return;
    }
    setSelectedRows((prevSelectedRows) => {
      const isRowAlreadySelected = prevSelectedRows.some((selectedRow) => selectedRow._id === row._id);
      const updatedSelection = isRowAlreadySelected
        ? prevSelectedRows.filter((selectedRow) => selectedRow._id !== row._id)
        : [...prevSelectedRows, row];

      return updatedSelection;
    });
  };

  const methods = useForm({
    resolver: yupResolver(Yup.object().shape({})),
    defaultValues: {
      date: getCurrentDateTime('date'),
      time: getCurrentDateTime('time'),
      bill: {
        out_sourced: 'No',
        rec_amt_1: 0,
        rec_amt_2: 0,
        tds_amt: 0,
        disc_amt: 0,
        gst_amt: 0,
        net_amt: 0
      }
    },
    mode: 'all'
  });
  const { handleSubmit, reset, setValue,watch } = methods;

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, 'bill', [
        '__v',
        '_id',
        'createdAt',
        'status',
        'service_items',
        'uhid'
      ]);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setValue('uhid', selectedRecord.uhid);
      setDetailList(selectedRecord.service_items)
      setValue('date', getCurrentDateTime('date', selectedRecord.createdAt));
      setValue('time', getCurrentDateTime('time', selectedRecord.createdAt));
    } else {
      reset();
    }
  }, [selectedRecord]);

  const formSubmit = async (formValues) => {
    const cleanedServiceItems = selectedRows.map(({ _id, ...rest }) => rest);
    setLoading(true);
    const payload = {
      uhid: formValues.uhid,
      service_items: cleanedServiceItems,
      bill_no: selectedRecord.bill_no,
      createdAt: selectedRecord.createdAt,
      ipd_no: selectedRecord.ipd_no || '',
      patient_name: selectedRecord.patient_name || 
      `${selectedRecord?.patient?.name_suffix} 
       ${selectedRecord?.patient?.first_name}
       ${selectedRecord?.patient?.last_name}`,
      age: selectedRecord.age,
      gender: selectedRecord.gender,
      doctor: selectedRecord.doctor,
      mobile_no: selectedRecord.mobile,
      barcode_no: selectedRecord.barcode_no,
      lab_bill_no: selectedRecord.bill_no
    };
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/lis-sample-collection-edit' : '/lis-sample-collection-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        ...payload,
        createdBy: user.name,
        updatedBy: editFlag ? user.name : '',
        organisation
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'edited' : 'created'} Sample Collected `, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      fetchData();
      setSelectedRows([])
    } else {
      toast(`Failed to ${editFlag ? 'edit' : 'create'} Sample Collection`, { variant: 'error' });
    }
    setLoading(false);
  };

  const watchSampleCollection = watch('collection_sample');

  useEffect(() => {
      fetchData();
      setSelectedRows([])
  }, [watchSampleCollection]); 

  const fetchData = async () => {
    setLoading(true);
    const query = {name : watchSampleCollection }
    try {
      const response = await request('/investigation-booking-opd-billing/list', {
        method: 'GET',
        query: query,
      });
      if (response.success) {
        setBillingData({
          loading: false,
          investigationBookings: response?.data?.investigationBookings || [],
          opdBills: response?.data?.opdBills || [],
          ipdBills: response?.data?.ipdBills || [],
        });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setBillingData({
          loading: false,
          investigationBookings: [],
          opdBills: [],
          ipdBills: [],
        });
      }
    } catch (error) {
      toast('Error in fetching data', { variant: 'error' });
      setBillingData({
        loading: false,
        investigationBookings: [],
        opdBills: [],
        ipdBills: [],
      });
    }
  };

  const combinedData = [
    ...billingData.investigationBookings.map(item => ({ ...item, type: 'Investigation Booking' })),
    ...billingData.opdBills.map(item => ({ ...item, type: 'Opd Billing' })),
    ...billingData.ipdBills.map(item => ({ ...item, type: 'Ipd Billing' }))
  ];
  
  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                Sample Collection
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={watchSampleCollection === 'All' || watchSampleCollection === 'Collected'}
                  onClick={handleSubmit(formSubmit)}
                >
                  Collection Sample
                </CustomButtonn>
                <CustomButtonn id={`${lastPathSegment}Print`} variant="contained">
                  Collect & Print
                </CustomButtonn>
                <CustomButtonn id={`${lastPathSegment}Print`} variant="contained">
                  Print
                </CustomButtonn>
                <CustomButtonn id={`${lastPathSegment}Print`} variant="contained">
                  TRF Print
                </CustomButtonn>
                <CustomButtonn id={'Exit'} variant="contained">
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={5} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>Collection Sample</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="collection_sample"
                      menus={COLLECTION_SAMPLE}
                      defaultValue="Pending"
                      onChange={(e) => setCollectionSampleFilter(e.target.value)}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={5} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>From Date</Typography>
                    <TextField sx={{ flex: 0.5 }} name="from_date" type="date" />
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                    <TextField sx={{ flex: 0.5 }} name="to_date" type="date" />
                  </Stack>
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Stack spacing={1} direction="row" justifyContent="space-between"></Stack>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, boxShadow: '0px 2px 2px #0099ff' }}
                  >
                    <Table
                      containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                      data={combinedData || []}
                      columns={BILL_TABLE_HEADER}
                      size="small"
                      onRowClick={(row) => {
                        setSelectedRecord(row);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.3,
                  marginTop: '10px',
                  boxShadow: '0px 2px 2px #0099ff'
                }}
              >
                <Table
                  containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                  data={detailList.map((data) => ({ _id: data.service, ...data }))}
                  columns={TEST_TABLE_HEADER}
                  size="small"
                  showCollected={true}
                  handleRowSelect={handleRowSelect}
                  isSelected={isSelected}
                />
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};
